/**
 * Insights slice.
 *
 * The Insights collector, and the insight-to-opportunity links drawn from it.
 *
 * Slices share one store: `set` and `get` are typed over the whole AppStore,
 * so an action here can read or write another slice's state where the domains
 * genuinely interact (loading a project resets the tree, converting a triage
 * item calls the tree's addNode).
 */
import type { StateCreator } from 'zustand';
import { canAddInsight } from '../../pricing/limits';
import { usePricingStore } from '../../pricing/pricingStore';
import { UpgradeMessageKey } from '../../pricing/upgradeMessages';
import type { InsightLink, InsightLinkDragState, InsightNode, NewInsightPayload } from '../../types';
import { randomId } from '../../utils/ostUtils';
import { DEFAULT_INSIGHT_CORNER_COLOR, initialData } from '../initialData';
import { patchData } from '../persistence';
import type { AppStore } from '../storeTypes';

export const createInsightsSlice: StateCreator<
    AppStore,
    [],
    [],
    Pick<
        AppStore,
        | 'insights'
        | 'insightLinks'
        | 'insightsPanelPosition'
        | 'insightsPanelMinimized'
        | 'insightLinkDrag'
        | 'addInsight'
        | 'updateInsight'
        | 'deleteInsight'
        | 'reorderInsights'
        | 'setInsightsPanelPosition'
        | 'setInsightsPanelMinimized'
        | 'beginInsightLinkDrag'
        | 'cancelInsightLinkDrag'
        | 'addInsightLink'
        | 'removeInsightLinkTarget'
    >
> = (set, get) => ({
    // ─── Insights Collector initial state ────────────────────────────────────
    insights: initialData.insights || [],

    insightLinks: initialData.insightLinks || [],
    insightsPanelPosition: initialData.insightsPanelPosition || { x: 20, y: 260 },

    // Default to collapsed on first load to avoid jarring slide-in transition
    insightsPanelMinimized: initialData.insightsPanelMinimized ?? true,


    // ─── Insight link drag — transient (never persisted) ─────────────────────
    insightLinkDrag: null,


    // ─── Insights Collector actions ──────────────────────────────────────────

    addInsight: (data: NewInsightPayload) => {
        set((state) => {
            const currentInsights = state.insights ?? [];

            // Insight cap — same predicate the InsightsPage pre-check uses.
            if (!canAddInsight(state.user, currentInsights.length)) {
                // Surface the pricing upgrade prompt and do not add the insight.
                usePricingStore.getState().showUpgradePrompt(UpgradeMessageKey.InsightLimit);
                return state;
            }

            const id = randomId();
            const newInsight: InsightNode = {
                id,
                type: 'insight',
                title: data.title || 'Untitled Insight',
                description: data.description || '',
                sourceUser: data.sourceUser,
                cornerColor: data.cornerColor || DEFAULT_INSIGHT_CORNER_COLOR,
                sourceUrl: data.sourceUrl,
                order: 0,
                createdAt: new Date().toISOString(),
            };
            const nextInsights = [newInsight, ...currentInsights].map((ins, i) => ({ ...ins, order: i }));
            patchData({ insights: nextInsights });
            return { insights: nextInsights };
        });
    },


    updateInsight: (id: string, data: Partial<Omit<InsightNode, 'id' | 'type'>>) => {
        set((state) => {
            const nextInsights = (state.insights ?? []).map((insight) =>
                insight.id === id ? { ...insight, ...data } : insight
            );
            patchData({ insights: nextInsights });
            return { insights: nextInsights };
        });
    },


    deleteInsight: (id: string) => {
        set((state) => {
            const filtered = (state.insights ?? []).filter((insight) => insight.id !== id);
            // Re-assign order values to keep them contiguous after deletion
            const nextInsights = filtered.map((insight, index) => ({ ...insight, order: index }));
            patchData({ insights: nextInsights });
            return { insights: nextInsights };
        });
    },


    reorderInsights: (fromIndex: number, toIndex: number) => {
        set((state) => {
            const insights = state.insights ?? [];
            if (
                fromIndex === toIndex ||
                fromIndex < 0 || fromIndex >= insights.length ||
                toIndex < 0 || toIndex >= insights.length
            ) {
                return state;
            }
            const next = [...insights];
            const [removed] = next.splice(fromIndex, 1);
            next.splice(toIndex, 0, removed);
            // Re-assign order to match the new visual positions
            const nextInsights = next.map((insight, index) => ({ ...insight, order: index }));
            patchData({ insights: nextInsights });
            return { insights: nextInsights };
        });
    },


    setInsightsPanelPosition: (pos: { x: number; y: number }) => {
        set({ insightsPanelPosition: pos });
        patchData({ insightsPanelPosition: pos });
    },


    setInsightsPanelMinimized: (minimized: boolean) => {
        set({ insightsPanelMinimized: minimized });
        patchData({ insightsPanelMinimized: minimized });
    },


    // ─── Insight Link actions ────────────────────────────────────────────────

    beginInsightLinkDrag: (drag: InsightLinkDragState) => {
        set({ insightLinkDrag: drag });
    },


    cancelInsightLinkDrag: () => {
        set({ insightLinkDrag: null });
    },


    addInsightLink: (
        insightId: string,
        selectedText: string,
        selectionStart: number,
        selectionEnd: number,
        targetNodeId: string,
    ) => {
        set((state) => {
            const currentLinks = state.insightLinks ?? [];

            // Check for an existing link with the same insight + character range
            const existingIndex = currentLinks.findIndex(
                (l) => l.insightId === insightId && l.selectionStart === selectionStart && l.selectionEnd === selectionEnd,
            );

            let nextLinks: InsightLink[];

            if (existingIndex !== -1) {
                const existing = currentLinks[existingIndex];
                // Do not add duplicate targets
                if (existing.targetNodeIds.includes(targetNodeId)) return state;
                const updated: InsightLink = { ...existing, targetNodeIds: [...existing.targetNodeIds, targetNodeId] };
                nextLinks = currentLinks.map((l, i) => (i === existingIndex ? updated : l));
            } else {
                const newLink: InsightLink = {
                    id: randomId(),
                    insightId,
                    selectedText,
                    selectionStart,
                    selectionEnd,
                    targetNodeIds: [targetNodeId],
                };
                nextLinks = [...currentLinks, newLink];
            }

            patchData({ insightLinks: nextLinks });
            return { insightLinks: nextLinks };
        });
    },


    removeInsightLinkTarget: (linkId: string, targetNodeId: string) => {
        set((state) => {
            const nextLinks = (state.insightLinks ?? [])
                .map((l) =>
                    l.id === linkId
                        ? { ...l, targetNodeIds: l.targetNodeIds.filter((id) => id !== targetNodeId) }
                        : l,
                )
                // Remove links with no remaining targets
                .filter((l) => l.targetNodeIds.length > 0);

            patchData({ insightLinks: nextLinks });
            return { insightLinks: nextLinks };
        });
    },
});
